import { z } from 'zod';

export const TESTSTUFE = ['umsetzung', 'integration', 'uat', 'go_live'] as const;
export type Teststufe = (typeof TESTSTUFE)[number];

export const SCOPE_MODE = ['processes', 'e2e', 'mix'] as const;
export type ScopeMode = (typeof SCOPE_MODE)[number];

// #127: Ein Plan im Modus „nur Prozesse" hat keine Ketten-Reihenfolge — kein Glied wartet, es gibt
// weder 409 predecessor_not_done noch Pflicht-Ergebnistext noch Pflicht-e2e_id. Alles andere als
// 'processes' (e2e, mix, und ein fehlender Wert in Mocks/Alt-Daten) bleibt gesperrt wie bisher.
// EINE Funktion für Server und Oberfläche, damit beide dieselbe Antwort geben.
export function chainOrderEnforced(scopeMode: ScopeMode | null | undefined): boolean {
  return scopeMode !== 'processes';
}

// EXE-5: mind. Successful / Error / Open / Blocked / Repeat.
export const TEST_RESULT = ['successful', 'error', 'open', 'blocked', 'repeat'] as const;
export type TestResult = (typeof TEST_RESULT)[number];

// #93: eigener Name für den Client-Spiegel von PlaylistEntry.last_result. Als Ableitung von
// TestResult (nicht als handkopierte Literal-Union) definiert, damit eine künftige Änderung an
// TEST_RESULT hier nicht still auseinanderdriftet — der eigene Name bleibt trotzdem bestehen, weil
// er semantisch etwas anderes markiert ("letztes Lauf-Ergebnis eines Playlist-Eintrags" statt
// "Laufergebnis beim Einreichen").
export type PlaylistLastResult = TestResult;

// EXE-6: Screenshots werden in V1 als data-URLs am Lauf gespeichert (kein Blob-Storage).
// Grenzwert bewusst konservativ; kann in Plan 7 (Projekt-Konfiguration) konfigurierbar werden.
export const SCREENSHOT_MAX_BYTES = 2_000_000;

// ISO-8601-Datum/Zeit als String; das API mappt auf timestamptz. `null` = nicht gesetzt.
const isoDateNullable = z.string().datetime().nullable().default(null);

// A9 (#114): Der Lebenszyklus eines Testplans ersetzt das frühere Boolean `is_active`. Ein
// Boolean kannte nur „an/aus" und konnte den Endzustand nicht ausdrücken — abgeschlossen und
// „noch nie aktiviert" sahen identisch aus, obwohl der eine gesperrt und der andere in Arbeit ist.
export const PLAN_STATUS = ['entwurf', 'aktiv', 'abgeschlossen'] as const;
export type PlanStatus = (typeof PLAN_STATUS)[number];

export const TestPlanCreateSchema = z.object({
  name: z.string().min(1),
  teststufe: z.enum(TESTSTUFE),
  scope_mode: z.enum(SCOPE_MODE).default('mix'),
  window_from: isoDateNullable,
  target_date: isoDateNullable,
});
// #114: `status` ist bewusst NICHT Teil von Create/Update. Der Übergang ist laut A9 „immer manuell
// durch den Testmanager" und hängt an Bedingungen (höchstens ein aktiver Plan, offene
// Bestätigungen) — als gewöhnliches Kopffeld wäre er über den Speichern-Knopf mitgeschrieben
// worden und hätte alle drei Bedingungen umgangen. Er läuft ausschließlich über
// PATCH /plans/:planId/status (PlanStatusChangeSchema unten).
export const TestPlanUpdateSchema = TestPlanCreateSchema.partial();

// #114: Zustandswechsel des Testplans. Die beiden Notizen sind bewusst getrennte Felder statt
// eines gemeinsamen `note`: sie begründen zwei verschiedene Dinge (Übersteuern trotz offener
// Bestätigungen vs. Wiedereröffnen eines Endzustands), und nur so kann der Server erkennen, dass
// der Aufrufer die Warnung wirklich gesehen hat, statt eine beliebige Notiz als Freigabe zu lesen.
// `.trim().min(1)` statt `.min(1)` — sonst zählte ein Leerzeichen als Begründung.
export const PlanStatusChangeSchema = z.object({
  status: z.enum(PLAN_STATUS),
  override_note: z.string().trim().min(1).optional(),
  reopen_note: z.string().trim().min(1).optional(),
});

// PLAN-2: Scope wird auf Testfall-Ebene gesetzt (E2E/Prozess-Auswahl löst das FE zu Testfällen auf).
export const PlanScopeSchema = z.object({ test_case_ids: z.array(z.string().uuid()) });

// PLAN-3: weiche Zuweisung eines Testers zu einem in-scope Testfall (tester_id null = Zuweisung entfernen).
export const AssignmentSchema = z.object({
  test_case_id: z.string().uuid(),
  tester_id: z.string().uuid().nullable(),
});

// #91: Sammelaktion "Tester zuweisen" — Bulk-Form von AssignmentSchema. Obergrenze 1000 =
// großzügig über dem realen Katalog (546), aber ein Riegel gegen unbegrenzte Bodies.
// Teilerfolg-Semantik: Objekt-Probleme werden übersprungen und benannt (Antwort
// { updated, skipped }); ZIEL-Probleme (ungültiger Tester/Severity) betreffen alle Objekte
// gleich und bleiben Request-Fehler (422) — wie beim Einzel-PATCH.
export const BulkAssignmentSchema = z.object({
  test_case_ids: z.array(z.string().uuid()).min(1).max(1000),
  tester_id: z.string().uuid().nullable(),
});

// EXE-4..7
export const RunSubmitSchema = z.object({
  test_case_id: z.string().uuid(),
  result: z.enum(TEST_RESULT),
  result_description: z.string().nullable().default(null),
  failed_step_id: z.string().uuid().nullable().default(null), // Fehler → betroffener Schritt (DEF-1-Naht, Plan 5)
  e2e_id: z.string().uuid().nullable().default(null), // E2E-Kontext-Attribut
  used_test_data_ids: z.array(z.string().uuid()).default([]),
  screenshots: z.array(z.string()).default([]), // data-URLs
  // #90: optionale Severity-Wahl des Testers im Fehler-Dialog. `null`/fehlend = kein bewusster
  // Vorschlag — der Auto-Defect fällt dann auf die Projekt-Default-Severity zurück und bleibt
  // untriagiert (severity_triaged bleibt false, siehe auto-defect.ts). Ein gesetzter Wert zählt
  // als Menschenentscheidung (severity_triaged: true) und wird server-seitig gegen das Projekt
  // geprüft (assertSeverityInProject), damit keine fremde Severity-ID durchrutscht.
  severity_id: z.string().uuid().nullable().default(null),
  // #126: Antwort auf „Hält der Fehler die E2E-Kette an?" / „Ist der Testfall damit nicht
  // bestanden?". true = Lauf `error`; false = Lauf `successful` MIT Defect („bestanden mit
  // Fehler"). null = Fehler-Dialog nicht benutzt oder alter Client — Verhalten wie bisher. Kein
  // Datenbankfeld: die Antwort ist aus dem Ergebnis des verknüpften Laufs ableitbar (defects.ts).
  stops_chain: z.boolean().nullable().default(null),
  // #208 Nachtrag: notierte Fehler dieser Durchführung, die der Lauf als Grund nennt — der Server
  // setzt ihr `test_run_id` auf den neuen Lauf. Nur bei `error`. Fehlt das Feld (alter Client),
  // wird nichts verknüpft.
  linked_defect_ids: z.array(z.string().uuid()).max(200).default([]),
  // #208 Nachtrag: false = keinen neuen Auto-Defect anlegen (der Grund steckt in den verknüpften).
  // Nur zulässig bei `error` mit mindestens einem verknüpften Defect. Fehlt das Feld: wie heute.
  create_defect: z.boolean().default(true),
});

// Task 7 (#113, A5): Der Notausgang fuer haengende Ketten —
// `POST .../plans/:planId/e2e/:e2eId/releases`. Plan und Kette stehen im Pfad, im Rumpf steht,
// WELCHES Glied freigegeben wird und WARUM.
//
// `.trim().min(1)` statt `.min(1)` (Muster: PlanStatusChangeSchema oben): Die Begruendung ist laut
// A5 Pflicht UND wird in der Kettenleiste angezeigt („freigegeben von Meier: Beleg 4711 manuell
// angelegt"). Ein Leerzeichen darf diese Pflicht nicht erfuellen. `.trim()` ist in zod zugleich
// eine Transformation — der geparste Wert ist bereits getrimmt und geht so in die Datenbank.
// Das ist die Lehre aus Task 5, wo `result_description` getrimmt GEPRUEFT und roh GESPEICHERT
// wurde: was geprueft wird, muss auch so gespeichert werden.
//
// Kein `released_by` im Rumpf: wer freigibt, ist der angemeldete Nutzer. Ein Feld dafuer waere ein
// Weg, eine fremde Unterschrift unter eine Freigabe zu setzen.
export const E2eReleaseCreateSchema = z.object({
  test_case_id: z.string().uuid(),
  reason: z.string().trim().min(1),
});
export type E2eReleaseCreate = z.infer<typeof E2eReleaseCreateSchema>;

// 3.2: Simplification (SAP Note) — kein Versioning/Kommentar/Anhang.
export const SimplificationCreateSchema = z.object({
  title: z.string().min(1),
  // Task B3 (#53A): getrennte *_ai-Abzeichen je Feld (Lehre #74, catalog-e2e.ts:98-106) — beim
  // "Übernehmen" eines KI-Vorschlags setzt das Frontend genau das Feld, das UNVERÄNDERT blieb.
  title_ai: z.boolean().default(false),
  description_html: z.string().nullable().default(null),
  description_ai: z.boolean().default(false),
  sap_note: z.string().nullable().default(null),
  node_id: z.string().uuid().nullable().default(null), // Teilprojekt-Zuordnung
  is_exception: z.boolean().default(false), // #33: bewusst ohne Testfall (Ausnahme-Zustand)
});
// Review I2: die *_ai-Abzeichen gehoeren im UPDATE-Pfad allein dem Server. `.partial()` allein
// schleppte sie in den PATCH-Body, und der Handler spreadet den ungefiltert ins Update — ein
// `PATCH {"title_ai": true}` haette das KI-Abzeichen auf einen von Hand getippten Titel gesetzt.
// Das Abzeichen ist die HITL-Herkunftsangabe in einem Dokument, das beim Kunden Teil der Abnahme
// wird; die Wertvergleichs-Logik im Handler LOESCHT es korrekt, schuetzt aber das SETZEN nicht.
// Kein legitimer Aufrufer verliert dadurch etwas: der Voll-Draft von CatalogTable
// (simplifications-tab.tsx `Draft`) fuehrt die Flags gar nicht, und Zod strippt unbekannte Keys
// still — ein Body mit title_ai laeuft also durch, nur ohne Wirkung, kein 400.
// Der CREATE-Pfad behaelt die Flags bewusst: nur der Client kennt im Moment des Anlegens, ob das
// Feld unveraendert aus dem KI-Vorschlag stammt (der Vorschlag existiert serverseitig nicht mehr —
// Entscheidung 1: nichts aus der Extraktion wird aufbewahrt). Das ist eine bewusste, einmalige
// Behauptung beim Anlegen durch jemanden mit `catalog.edit`; danach ist das Flag server-eigen.
export const SimplificationUpdateSchema = SimplificationCreateSchema.omit({
  title_ai: true,
  description_ai: true,
}).partial();
export const SimplificationLinkSchema = z.object({ test_case_ids: z.array(z.string().uuid()) });

// #53A: Request-Body von POST /simplifications:extract — der vom Nutzer eingefügte SAP-Note-Text.
// Obergrenze wie DashboardBriefingRequestSchema (dashboard.ts): verhindert absurd große Bodies,
// die ungeprüft ins LLM-Prompt liefen (der Guardrail-Filter greift zusätzlich). min(20): ein
// Kürzel/leeres Feld ist erkennbar kein Note-Text, das lohnt keinen LLM-Call.
export const SimplificationExtractRequestSchema = z.object({
  note_text: z.string().min(20).max(100_000),
});
export type SimplificationExtractRequest = z.infer<typeof SimplificationExtractRequestSchema>;

// Antwort: das abgeleitete Ergebnis oder null. `reason` unterscheidet die Fehl-Ausgänge, weil sie
// fachlich VERSCHIEDEN sind (Review I1): 'unavailable' = kein Gateway/Laufzeitfehler (die KI kam
// nicht zustande), 'no_content' = die KI antwortete und meldet, dass sie den Text nicht verwerten
// kann, 'rejected' = der Injection-Filter hat den Text abgewiesen, er ging nie zur KI. Ein
// gemeinsames `null` hätte das Panel gezwungen, in zwei von drei Fällen etwas Unwahres zu melden.
// Der eingefügte Note-Text selbst ist NIE Teil der Antwort — er wird nirgends gespeichert oder
// gespiegelt (siehe Task-B3-Brief).
export type SimplificationExtractReason = 'ok' | 'unavailable' | 'no_content' | 'rejected';

export interface SimplificationExtractResponse {
  result: { title: string; description: string } | null;
  reason: SimplificationExtractReason;
}

// PLAN-5
export const SubstitutionCreateSchema = z
  .object({
    absent_user_id: z.string().uuid(),
    substitute_user_id: z.string().uuid(),
    from_date: z.string().datetime(),
    to_date: z.string().datetime(),
  })
  // #50: zwei widersprüchliche Zustände verhindern, die sonst still folgenlos gespeichert würden
  // (AC "Überlappende oder rückwirkende Zeiträume führen nicht zu widersprüchlichen Zuständen").
  // Überlappungen selbst sind erlaubt — die Vertretungsliste ist eine Vereinigung; nur diese zwei
  // Fälle sind echte Fehler.
  .superRefine((v, ctx) => {
    if (v.absent_user_id === v.substitute_user_id) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['substitute_user_id'],
        message: 'substitute_equals_absent',
      });
    }
    if (new Date(v.from_date).getTime() > new Date(v.to_date).getTime()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['to_date'],
        message: 'to_before_from',
      });
    }
  });

// #46: Projektstruktur pflegbar. Anlegbar sind nur `teilprojekt` und `stream` — `modul` existiert
// zwar im DB-Enum, ist aber tot (Module hängen an der separaten `modules`-Tabelle, nicht an
// Struktur-Knoten). Hierarchie (parent_node_id) bleibt in V1 bewusst flach (Nutzerentscheidung:
// weder Rechte-Scope noch Sign-off aggregieren heute über Kinder), deshalb kein parent im Schema.
export const STRUCTURE_NODE_KINDS = ['teilprojekt', 'stream'] as const;
export const StructureNodeCreateSchema = z.object({
  kind: z.enum(STRUCTURE_NODE_KINDS),
  name: z.string().trim().min(1),
});
export const StructureNodeUpdateSchema = z.object({
  name: z.string().trim().min(1),
});

export type TestPlanCreate = z.infer<typeof TestPlanCreateSchema>;
// #114: der Rumpf von PATCH /plans/:planId/status.
export type PlanStatusChange = z.infer<typeof PlanStatusChangeSchema>;
export type RunSubmit = z.infer<typeof RunSubmitSchema>;
export type SimplificationCreate = z.infer<typeof SimplificationCreateSchema>;
export type StructureNodeCreate = z.infer<typeof StructureNodeCreateSchema>;
